import { InviteLock } from "@/components/growth/InviteLock";
import { openShare } from "@/lib/share";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Trophy, Sparkles, Share2, RotateCcw, X, Play, Flame, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { HeartRunnerCanvas } from "@/components/runner/HeartRunnerGame";
import { RunBoard, HowTo, RunDuelPanel } from "@/components/runner/RunnerParts";
import { UserAvatar } from "@/components/UserBits";
import { MusicToggle } from "@/components/games/GameMusic";

const shareLink = (code) => `${window.location.origin}/herz-laeufer?ref=${code || ""}`;
const share = (code, text) => openShare({ kind: "game", target: "runner", text, title: "Freunde herausfordern", msg: text, fallbackUrl: shareLink(code) });

export const Title = ({ size = "text-6xl" }) => (
  <h1 className={`rn-title font-display ${size} font-black leading-[0.9] italic`} data-testid="runner-title">Herz-<br /><span>Läufer</span></h1>
);

function Guest() {
  const [sp] = useSearchParams();
  const ref = sp.get("ref") || "";
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/runner/public?ref=${encodeURIComponent(ref)}`).then(({ data }) => setD(data)).catch(() => setD({})); }, [ref]);
  useEffect(() => { sessionStorage.setItem("after_auth", "/herz-laeufer"); window.scrollTo(0, 0); }, []);
  return (
    <div className="relative min-h-[100dvh] overflow-hidden rn-bg" data-testid="runner-guest">
      <div className="relative h-[50vh] max-h-[520px]"><HeartRunnerCanvas demo testId="runner-demo" /><div className="absolute inset-0 bg-gradient-to-t from-[#0b0420] via-transparent to-transparent pointer-events-none" /></div>
      <div className="relative z-10 -mt-16 flex flex-col p-5 pb-28">
        <div className="max-w-md mx-auto w-full space-y-4 stagger">
          <p className="text-[11px] font-extrabold tracking-[0.3em] text-pink-300">ITZPLAY · GAME</p>
          <Title />
          {d?.inviter ? (
            <div className="rn-glass flex items-center gap-3 rounded-3xl p-3" data-testid="runner-challenge">
              <UserAvatar name={d.inviter.name} picture={d.inviter.picture} size="w-12 h-12" noStory />
              <p className="text-white text-sm"><b>{d.inviter.name}</b> fordert dich heraus{d.inviter.best ? <> – Rekord: <b className="rn-gold-text">{d.inviter.best} Punkte</b></> : ""}. Läufst du weiter?</p>
            </div>
          ) : <p className="text-white/80">Lauf mit deinem Herz über drei Neon-Spuren, wisch an Geschenk-Waggons vorbei, spring über Dornen und sammle Gold-Herzen – für echte Punkte auf deinem Konto.</p>}
          {d?.enabled === false && <p className="text-amber-200 text-sm font-bold">Das Spiel ist gerade pausiert.</p>}
          <Link to={`/registrieren?${ref ? `ref=${encodeURIComponent(ref)}&` : ""}source=herz-laeufer`} data-testid="runner-register" className="rn-btn flex items-center justify-center gap-2 h-14 rounded-full text-white text-lg font-black active:scale-95 transition-transform">
            <Play className="w-5 h-5" fill="currentColor" /> Kostenlos registrieren & loslaufen
          </Link>
          <Link to="/login" data-testid="runner-login" className="block text-center text-sm font-bold text-white/80">Schon Mitglied? Einloggen</Link>
          {d?.top?.length > 0 && <div className="pt-1"><p className="text-xs font-extrabold text-white/60 mb-2 flex items-center gap-1"><Trophy className="w-3.5 h-3.5" /> BESTENLISTE</p><RunBoard rows={d.top} testId="runner-public-board" /></div>}
        </div>
      </div>
    </div>
  );
}

function Over({ res, code, onAgain, onClose }) {
  const earned = (res.points || 0) + (res.bonus || 0);
  return (
    <div className="absolute inset-0 z-20 grid place-items-center bg-[#0b0420]/70 backdrop-blur-md p-5" data-testid="runner-over">
      <div className="w-full max-w-sm rounded-[2.25rem] rn-card p-6 text-center text-white th-pop">
        {res.record && <p className="mx-auto mb-2 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-300 to-amber-500 px-3 py-1 text-xs font-black text-amber-950" data-testid="runner-record"><Flame className="w-3.5 h-3.5" /> NEUER REKORD</p>}
        <p className="text-xs font-extrabold tracking-[0.25em] text-pink-200">{res.record ? "WAHNSINN!" : "LAUF BEENDET"}</p>
        <p className="font-display text-7xl font-black tabular-nums rn-gold-text leading-none mt-1" data-testid="runner-final-score">{res.score}</p>
        <p className="mt-1 text-sm text-white/70">{res.meters ?? 0} m · {res.coins} Gold-Herzen · Bestleistung {res.best}</p>
        {res.today_rank && <p className="mt-1 text-sm font-bold text-amber-300" data-testid="runner-rank">Platz {res.today_rank} heute</p>}
        <p className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-white/10 ring-1 ring-white/15 px-3.5 py-1.5 text-sm font-bold" data-testid="runner-earned">
          <Sparkles className="w-4 h-4 text-amber-300" /> +{earned} Punkte{res.bonus ? " inkl. Rekord-Bonus" : ""} · heute {res.today_points}/{res.daily_cap}
        </p>
        {!earned && res.today_points < res.daily_cap && <p className="mt-2 text-xs text-white/55" data-testid="runner-earn-hint">Ab 30 Spielpunkten gibt es Community-Punkte.</p>}
        <div className="mt-5 grid gap-2">
          <button onClick={onAgain} data-testid="runner-again" className="rn-btn h-13 py-3.5 rounded-full font-black inline-flex items-center justify-center gap-2 active:scale-95 transition-transform"><RotateCcw className="w-5 h-5" /> Nochmal laufen</button>
          <button onClick={() => share(code, `Ich bin beim „Herz-Läufer“ auf ${res.score} Punkte gekommen 💗 Schaffst du mehr?`)} data-testid="runner-share" className="h-12 rounded-full bg-white/10 ring-1 ring-white/20 font-bold inline-flex items-center justify-center gap-2"><Share2 className="w-5 h-5" /> Freunde herausfordern</button>
          <button onClick={onClose} data-testid="runner-close" className="h-10 text-sm font-bold text-white/70">Zurück zur Lobby</button>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ label, value, sub, testId, icon: I }) => (
  <div className="rn-glass rounded-3xl p-4">
    <p className="text-[10px] font-extrabold tracking-[0.2em] text-white/55 flex items-center gap-1"><I className="w-3 h-3" /> {label}</p>
    <p className="mt-1 text-3xl font-black tabular-nums text-white" data-testid={testId}>{value}{sub && <span className="text-base text-white/45">{sub}</span>}</p>
  </div>
);

function Member() {
  const [d, setD] = useState(null);
  const [run, setRun] = useState(null);
  const [res, setRes] = useState(null);
  const [tab, setTab] = useState("today");
  const load = () => api.get("/runner").then(({ data }) => setD(data)).catch((e) => setD({ err: formatErr(e) }));
  useEffect(() => { load(); sessionStorage.removeItem("after_auth"); window.scrollTo(0, 0); }, []);
  const start = async () => {
    try { const { data } = await api.post("/runner/start"); setRes(null); setRun({ id: data.session_id, k: Date.now() }); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const over = async ({ score, coins, meters }) => {
    try { const { data } = await api.post("/runner/finish", { session_id: run.id, score, coins }); setRes({ ...data, meters }); load(); }
    catch (e) { toast.error(formatErr(e)); setRes({ score, coins, meters, best: Math.max(score, d?.best || 0), today_points: d?.today_points || 0, daily_cap: d?.daily_cap || 50 }); }
  };
  const close = () => { setRun(null); setRes(null); };
  if (run) {
    return (
      <div className="fixed inset-0 z-[80] rn-bg select-none" data-testid="runner-play">
        <div className="relative h-full max-w-[560px] mx-auto">
          <HeartRunnerCanvas key={run.k} onOver={over} />
          <div className="absolute top-4 left-4 z-10"><MusicToggle dark compact /></div>
          <button onClick={close} aria-label="Beenden" data-testid="runner-quit" className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/10 backdrop-blur text-white grid place-items-center"><X className="w-5 h-5" /></button>
          {res && <Over res={res} code={d?.code} onAgain={start} onClose={close} />}
        </div>
      </div>
    );
  }
  const rows = tab === "today" ? d?.today_top : tab === "week" ? d?.week_top : d?.top;
  return (
    <div className="min-h-[100dvh] rn-bg text-white" data-testid="runner-lobby">
      <div className="relative h-[52vh] max-h-[520px] overflow-hidden">
        <HeartRunnerCanvas demo testId="runner-demo" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0b0420] via-[#0b0420]/10 to-transparent pointer-events-none" />
        <Link to="/spiele" data-testid="runner-back" className="absolute top-4 left-4 h-10 px-4 rounded-full rn-glass text-sm font-bold grid place-items-center">← Spiele</Link>
        <div className="absolute top-4 right-4"><MusicToggle dark /></div>
      </div>
      <div className="max-w-md mx-auto px-5 -mt-28 relative space-y-5 pb-28">
        <InviteLock gate="games" dark />
        <div className="stagger space-y-3">
          <span className="inline-flex items-center gap-1.5 rounded-full rn-glass px-3 h-7 text-[11px] font-black tracking-[0.2em] text-pink-200"><Zap className="w-3.5 h-3.5 text-amber-300" /> NEU · 3D-RUNNER</span>
          <Title />
          <p className="text-white/75 text-sm max-w-xs">Wechsle zwischen drei Spuren, weich Geschenk-Waggons aus, spring über Dornen, roll unter Bannern durch und sammle Gold-Herzen. Je weiter du läufst, desto schneller wird's.</p>
        </div>
        {d?.err && <p className="text-amber-200 font-bold" data-testid="runner-error">{d.err}</p>}
        <div className="grid grid-cols-2 gap-3">
          <Stat icon={Trophy} label="BESTLEISTUNG" value={d?.best ?? 0} testId="runner-best" />
          <Stat icon={Sparkles} label="PUNKTE HEUTE" value={d?.today_points ?? 0} sub={`/${d?.daily_cap ?? 50}`} testId="runner-today" />
        </div>
        <button onClick={start} disabled={!d || d.err} data-testid="runner-start" className="rn-btn rn-pulse w-full h-16 rounded-full text-xl font-black inline-flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50">
          <Play className="w-6 h-6" fill="currentColor" /> Loslaufen
        </button>
        <p className="text-center text-[11px] text-white/50 -mt-2">Je {d?.score_per_point ?? 30} Spielpunkte = 1 Community-Punkt · max. {d?.daily_cap ?? 50} am Tag</p>
        <HowTo />
        <button onClick={() => share(d?.code, "Lauf mit mir beim „Herz-Läufer“ 💗")} data-testid="runner-invite" className="w-full h-12 rounded-full rn-glass font-bold inline-flex items-center justify-center gap-2"><Share2 className="w-5 h-5" /> Freunde per Link herausfordern</button>
        {d && !d.err && <RunDuelPanel />}
        <div>
          <p className="font-display text-xl font-black mb-3 flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-300" /> Bestenliste</p>
          <div className="rn-glass rounded-full p-1 grid grid-cols-3 mb-3">
            {[["today", "Heute"], ["week", "Woche"], ["all", "Allzeit"]].map(([k, l]) => <button key={k} onClick={() => setTab(k)} data-testid={`runner-tab-${k}`} className={`h-9 rounded-full text-sm font-black transition-colors ${tab === k ? "rn-btn" : "text-white/65"}`}>{l}</button>)}
          </div>
          <RunBoard rows={rows} testId="runner-board" />
        </div>
      </div>
    </div>
  );
}

export default function HeartRunner() {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-[100dvh] rn-bg" />;
  return user ? <Member /> : <Guest />;
}
